import React, { useState, useEffect } from 'react';
import { Users, UserPlus, 
  Award, Shield, Clock, CheckCircle, 
  Phone, Mail, MapPin, Send, ArrowRight, Home, BookOpen,
  Brain, Target, Zap, Star, TrendingUp, Globe, Lock,
  FileText, Scale,  Building, Gavel, Heart,
  Baby, UserCheck, Users as FamilyMembers, Shield as Protection,
  GraduationCap, Briefcase, Calendar, UserCheck as Student,
  Building2, Users as Team, Globe as World, CheckSquare,
  ArrowUpRight, FileText as Report, Users as Supervisor
} from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CooperativeTraining = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [visibleSections, setVisibleSections] = useState({
    hero: false,
    about: false,
    objectives: false,
    stages: false,
    benefits: false,
    process: false,
    contact: false
  });

  useEffect(() => {
    setIsVisible(true);
    
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section');
          if (sectionId) {
            setVisibleSections(prev => ({
              ...prev,
              [sectionId]: true
            }));
          }
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe all sections
    const sections = document.querySelectorAll('[data-section]');
    sections.forEach(section => observer.observe(section));

    return () => {
      sections.forEach(section => observer.unobserve(section));
    };
  }, []);

  const whatsappUrl = "https://wa.me/966115115945";

  const trainingServices = [
    {
      icon: GraduationCap,
      title: "إعداد برامج التدريب التعاوني",
      description: "تصميم برامج تدريبية مخصصة تناسب احتياجات الطلاب والمؤسسات",
      image: "/images/services/91.png"
    },
    {
      icon: Building,
      title: "تسهيل الاتفاقيات",
      description: "إعداد وتوقيع الاتفاقيات بين الجامعات وجهات التدريب",
      image: "/images/services/92.png"
    },
    {
      icon: UserPlus,
      title: "إشراف ومتابعة",
      description: "إشراف أكاديمي وميداني شامل على برامج التدريب التعاوني",
      image: "/images/services/93.png"
    }
  ];

  const trainingObjectives = [
    "ربط الجانب النظري بالتطبيق العملي في بيئة العمل الحقيقية",
    "إكساب المتدرب مهارات مهنية متقدمة مرتبطة بتخصصه الأكاديمي",
    "تعريف الطالب بثقافة بيئة العمل وأنظمتها وقوانينها",
    "بناء شبكة علاقات مهنية تساعده في حياته العملية المستقبلية",
    "تطوير مهارات التواصل والعمل الجماعي في بيئة مهنية",
    "إعداد الطالب لسوق العمل من خلال الخبرة العملية المباشرة"
  ];

  const trainingStages = [
    {
      step: "01",
      title: "التحضير والتخطيط",
      description: "اختيار جهة التدريب المناسبة وتوقيع الاتفاقيات",
      icon: FileText,
      details: [
        "تقييم احتياجات التدريب للطلاب",
        "اختيار جهات التدريب المناسبة",
        "إعداد وتوقيع الاتفاقيات التعاونية",
        "تحديد الأهداف والمخرجات المتوقعة"
      ]
    },
    {
      step: "02",
      title: "التنفيذ والمتابعة",
      description: "مباشرة العمل في جهة التدريب تحت الإشراف المباشر",
      icon: Briefcase,
      details: [
        "مباشرة العمل في البيئة المهنية",
        "الالتزام بساعات العمل والمهام المحددة",
        "متابعة دورية من المشرفين الأكاديمي والميداني",
        "تقييم الأداء وتقديم التغذية الراجعة"
      ]
    },
    {
      step: "03",
      title: "التقييم والتقارير",
      description: "إعداد التقارير النهائية وتقييم الأداء الشامل",
      icon: Report,
      details: [
        "إعداد تقارير دورية عن التقدم",
        "تقرير نهائي شامل من المتدرب",
        "تقييم من المشرف الميداني",
        "تقييم نهائي من المشرف الأكاديمي"
      ]
    },
    {
      step: "04",
      title: "المتابعة والتطوير",
      description: "متابعة الخريجين وتطوير البرامج التدريبية",
      icon: TrendingUp,
      details: [
        "متابعة أداء الخريجين في سوق العمل",
        "تطوير البرامج بناءً على التغذية الراجعة",
        "تحديث الاتفاقيات مع جهات التدريب",
        "تطوير معايير التقييم والجودة"
      ]
    }
  ];

  const trainingBenefits = [
    "اكتساب خبرة عملية حقيقية في مجال التخصص",
    "تعزيز المهارات المهنية والعملية",
    "بناء شبكة علاقات مهنية واسعة",
    "زيادة فرص التوظيف بعد التخرج",
    "تطوير مهارات حل المشكلات العملية",
    "فهم متطلبات سوق العمل الحقيقية"
  ];



  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100" dir="rtl">
      {/* Header */}
      <Header />
      
      {/* Hero Section */}
      <div 
        data-section="hero"
        className={`relative py-12 sm:py-24 overflow-hidden transition-all duration-1000 mt-16 sm:mt-20 ${visibleSections.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        style={{ backgroundColor: '#0a3a23' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='5'/%3E%3Ccircle cx='53' cy='7' r='5'/%3E%3Ccircle cx='30' cy='30' r='5'/%3E%3Ccircle cx='7' cy='53' r='5'/%3E%3Ccircle cx='53' cy='53' r='5'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
        
        <div className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center text-white transition-all duration-1000 delay-300 ${visibleSections.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="mb-4 sm:mb-8">
            <span className="text-white text-lg sm:text-xl animate-pulse">خدمات قانونية متخصصة</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6">
            <span className="text-white">التدريب التعاوني</span>
            <div className="w-20 sm:w-32 h-1 bg-white mx-auto mt-2 sm:mt-4 animate-pulse"></div>
          </h1>
          
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl opacity-90 max-w-2xl sm:max-w-4xl mx-auto leading-relaxed mb-4 sm:mb-8 px-4">
            نقدم خدمات متخصصة في إعداد وتنظيم برامج التدريب التعاوني
          </p>
          <p className="text-sm sm:text-base md:text-lg opacity-80 max-w-xl sm:max-w-3xl mx-auto px-4">
            نحرص على ربط الدراسة الأكاديمية بالتجربة الميدانية لضمان نجاح الطلاب في حياتهم المهنية
          </p>
        </div>

        {/* Navigation Breadcrumb */}
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-1 sm:gap-2 text-white text-xs sm:text-sm">
          <Link to="/" className="flex items-center gap-1 hover:text-gray-200 transition-colors">
            <Home className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>الرئيسية</span>
          </Link>
          <span>/</span>
          <div className="flex items-center gap-1">
            <GraduationCap className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>التدريب التعاوني</span>
          </div>
        </div>
      </div>

      {/* About Section */}
      <div data-section="about" className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div className={`transition-all duration-1000 ${visibleSections.about ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 sm:mb-8">
                ما هو التدريب التعاوني؟
              </h2>
              <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                <p>
                  التدريب التعاوني هو برنامج عملي تطبقه كثير من الجامعات والكليات، يهدف إلى ربط الدراسة الأكاديمية بالتجربة الميدانية، بحيث يقضي الطالب أو الطالبة فترة في بيئة عمل حقيقية قبل التخرج.
                </p>
                <p>
                  يتم التدريب التعاوني تحت إشراف مشرف أكاديمي من الجامعة ومشرف ميداني من جهة التدريب، مما يضمن تحقيق الأهداف التعليمية والمهنية المرجوة.
                </p>
                <div className="p-4 sm:p-6 rounded-xl border-r-4" style={{ backgroundColor: 'rgba(10, 58, 35, 0.1)', borderColor: '#0a3a23' }}>
                  <strong style={{ color: '#0a3a23' }}>فريقنا المتخصص في التدريب التعاوني يحرص على تقديم خدمات متميزة تضمن نجاح الطلاب في حياتهم المهنية.</strong>
                </div>
              </div>
            </div>
            
                                                                                                       <div className={`relative transition-all duration-1000 delay-300 ${visibleSections.about ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                                        <img
                         src="/images/صور الموقع (1)/13.jpg"
                         alt="التدريب التعاوني"
                         className="w-full h-auto rounded-xl sm:rounded-2xl shadow-2xl"
                         style={{ minHeight: '400px', objectFit: 'cover' }}
                       />
               </div>
          </div>
        </div>
      </div>

             {/* Objectives Section */}
       <div data-section="objectives" className="py-12 sm:py-20 bg-white">
         <div className="max-w-6xl mx-auto px-4 sm:px-6">
           <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${visibleSections.objectives ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
             <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
               أهداف التدريب التعاوني
             </h2>
             <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto px-4">
               نسعى لتحقيق أهداف شاملة ومتكاملة في التدريب التعاوني
             </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
             {trainingObjectives.map((objective, index) => (
               <div 
                 key={index} 
                 className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 transform hover:scale-105 ${visibleSections.objectives ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                 style={{ transitionDelay: `${index * 150}ms` }}
               >
                                                                                                                                                                                                                                                                                               <div className="relative h-32 sm:h-40 overflow-hidden">
                       <img 
                         src={[
                           "/images/services/141.png",
                           "/images/services/142.png",
                           "/images/services/143.png",
                           "/images/services/144.png",
                           "/images/services/151.png",
                           "/images/services/152.png"
                         ][index]}
                         alt={objective}
                         className="w-full h-full object-cover"
                       />
                       <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                     </div>
                 <div className="p-4 sm:p-6">
                   <div className="flex items-start gap-3 sm:gap-4">
                     <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 mt-1 flex-shrink-0" style={{ color: '#0a3a23' }} />
                     <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{objective}</p>
                   </div>
                 </div>
               </div>
             ))}
           </div>
         </div>
       </div>

      {/* Training Services */}
      <div data-section="stages" className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${visibleSections.stages ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              خدمات التدريب التعاوني
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto px-4">
              نقدم خدمات شاملة ومتكاملة في مجال التدريب التعاوني
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {trainingServices.map((service, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-xl sm:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden group transform hover:scale-105 ${visibleSections.stages ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="relative h-40 sm:h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-3 sm:bottom-4 right-3 sm:right-4">
                    <service.icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                </div>
                
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3 group-hover:text-[#0a3a23] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Training Stages */}
      <div data-section="process" className="py-12 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${visibleSections.process ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              مراحل التدريب التعاوني
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto px-4">
              عملية منظمة ومهنية لضمان نجاح التدريب التعاوني
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {trainingStages.map((stage, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-xl sm:rounded-2xl shadow-lg p-6 sm:p-8 transition-all duration-500 transform hover:scale-105 ${visibleSections.process ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                <div className="flex items-center gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0a3a23' }}>
                    <stage.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl sm:text-3xl font-bold text-[#0a3a23] mb-1">{stage.step}</div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">{stage.title}</h3>
                  </div>
                </div>
                
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                  {stage.description}
                </p>
                
                <ul className="space-y-2 sm:space-y-3">
                  {stage.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-2 sm:gap-3">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 mt-1 flex-shrink-0 text-[#0a3a23]" />
                      <span className="text-sm sm:text-base text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

             {/* Benefits */}
       <div data-section="benefits" className="py-12 sm:py-20">
         <div className="max-w-6xl mx-auto px-4 sm:px-6">
           <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${visibleSections.benefits ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
             <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
               مميزات التدريب التعاوني
             </h2>
             <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto px-4">
               نتميز بخبرة واسعة وخدمة متميزة في مجال التدريب التعاوني
             </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
             {trainingBenefits.map((benefit, index) => (
               <div 
                 key={index} 
                 className={`bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 transform hover:scale-105 ${visibleSections.benefits ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                 style={{ transitionDelay: `${index * 150}ms` }}
               >
                                                                                                                                                                                                                                                                                               <div className="relative h-32 sm:h-40 overflow-hidden">
                       <img 
                         src={[
                           "/images/services/94.png",
                           "/images/services/154.png",
                           "/images/services/21.png",
                           "/images/services/81.png",
                           "/images/services/82.png",
                           "/images/services/83.png"
                         ][index]}
                         alt={benefit}
                         className="w-full h-full object-cover"
                       />
                       <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                     </div>
                 <div className="p-4 sm:p-6">
                   <div className="flex items-start gap-3 sm:gap-4">
                     <Star className="w-5 h-5 sm:w-6 sm:h-6 mt-1 flex-shrink-0" style={{ color: '#0a3a23' }} />
                     <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{benefit}</p>
                   </div>
                 </div>
               </div>
             ))}
           </div>
         </div>
       </div>

      

      {/* Contact CTA */}
      <div className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
            نوفر خدمات متخصصة في التدريب التعاوني
          </h2>
          <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8">
            عبر البريد الالكتروني أو الاجتماعات أو الهاتف
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href={`${whatsappUrl}?text=أريد استشارة في التدريب التعاوني`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 sm:gap-3 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
              style={{ backgroundColor: '#0a3a23' }}
            >
              <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              تواصل عبر الواتساب
            </a>
            <Link
              to="/Contact"
              className="inline-flex items-center gap-2 sm:gap-3 bg-gray-800 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-gray-900 transition-all duration-300 shadow-lg hover:shadow-xl w-full sm:w-auto"
            >
              <Send className="w-5 h-5 sm:w-6 sm:h-6" />
              تواصل معنا
            </Link>
          </div>
        </div>
      </div>

      {/* Floating Contact Buttons */}
      <a
        href="tel:920033053"
        className="fixed bottom-20 sm:bottom-28 left-4 sm:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      {/* Floating WhatsApp Button */}
      <a
        href={`${whatsappUrl}?text=أريد استشارة في التدريب التعاوني`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 sm:bottom-6 left-4 sm:left-6 bg-gradient-to-r from-[#0a3a23] to-[#0a3a23] text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-[#0a3a23]/25 transition-all duration-300 z-50 hover:scale-110"
      >
        <WhatsAppIcon className="w-5 h-5 sm:w-6 sm:h-6" />
      </a>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CooperativeTraining;
